// Wraps any homepage module with feature-flag aware behaviour.
//
//   <FeatureSection sectionKey="quiz">
//     <QuizModule />
//   </FeatureSection>
//
// Behaviour by status:
//   live        — render children (default when flag is missing)
//   coming_soon — render a small placeholder card; show coming_soon_message
//                 when present, fall back to a generic line
//   hidden      — render nothing
//
// The wrapper intentionally has no size of its own when status is
// 'coming_soon' or 'hidden' so it can sit anywhere in the layout
// (sidebar, page-grid main column, …) without imposing its own
// dimensions.

import { type ReactNode } from 'react';
import { useFeatureFlags } from '../lib/useFeatureFlags';
import { useSectionView } from '../lib/useSectionView';
import { usePreviewBypass } from '../lib/usePreviewBypass';
import './FeatureSection.css';

// Preview-bypass: when the editor visits /?preview=1, every section
// renders as if its flag were 'live' for that browser, regardless of
// what the DB says. Hook moved to src/lib/usePreviewBypass.ts so the
// PreviewBanner can also detect bypass mode + offer a "Clear" button.

interface Props {
  sectionKey: string;
  /** Optional human-readable label for the placeholder, e.g. "Quiz".
   *  Falls back to a sentence-cased section key. */
  label?: string;
  children: ReactNode;
}

export default function FeatureSection({ sectionKey, label, children }: Props) {
  const flags = useFeatureFlags();
  const flag = flags[sectionKey];
  const previewBypass = usePreviewBypass();
  // Default to 'live' when the flag map hasn't loaded yet — never block
  // a section from rendering because of a slow API call.
  // Preview-bypass also forces 'live' so the editor can test
  // coming_soon / hidden sections on develop without DB changes.
  const status = previewBypass ? 'live' : (flag?.status ?? 'live');
  // Track first-view of each section for the /admin/metrics dashboard.
  // The wrapper div is invisible (display:contents) so it doesn't
  // affect layout; the IntersectionObserver fires once per session
  // per sectionKey then disconnects.
  const observe = useSectionView<HTMLDivElement>(sectionKey);

  if (status === 'hidden') return null;

  if (status === 'coming_soon') {
    const niceLabel = label ?? humanise(sectionKey);
    return (
      <div ref={observe} className="feature-coming-wrap" aria-label={`${niceLabel} — coming soon`}>
        <div className="feature-coming-wrap__content" aria-hidden="true">
          {children}
        </div>
        <div className="feature-coming-wrap__overlay">
          <span className="feature-coming__badge">Coming soon</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={observe} style={{ display: 'contents' }}>
      {children}
    </div>
  );
}

function humanise(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
